import { useRouter } from '@boss/hooks';
import { formatPrice } from '@boss/utils';
import { cva } from 'class-variance-authority';
import { twMerge } from 'tailwind-merge';

// Used in price, no relevance to this file
type ProductUnit = '1L';

export type PriceProps = {
  className?: string;
  discountPercentage?: number;
  quantity?: ProductUnit;
  strikePrice?: number;
  value?: number;
  privatePrice?: number;
  translations?: {
    quantityPrefix?: string;
    quantitySufix?: string;
    strikePrice?: string;
    discount?: string;
    unitLabel?: string;
  };
  prefix?: boolean;
  variant?: 'base' | 'strike' | 'info';
  size?: 'medium' | 'large' | 'small' | 'xSmall';
  font?: 'alt' | 'raleway' | 'sans';
  valueClassName?: string;
  discountInfo?: string;
};

export const PriceValueStyles = cva('pricing', {
  variants: {
    priceVariant: {
      base: 'text-price-base',
      strike: 'text-price-strike text-green',
      info: 'text-price-info',
    },
    size: {
      xSmall: 'body',
      small: 'text-lg',
      medium: 'pricing',
      large: 'pricingLarge',
    },
    font: {
      alt: 'font-alt',
      sans: 'font-monaSans',
      raleway: 'font-raleway',
    },
  },
});

const Price = ({
  className,
  quantity,
  strikePrice,
  value,
  size = 'medium',
  prefix,
  variant,
  privatePrice,
  translations,
  font = 'sans',
  valueClassName,
  discountInfo,
}: PriceProps) => {
  const { locale } = useRouter();
  const captionStyling = 'caption text-gray-dark';

  const getPriceStylingVariant = () => {
    if (variant) {
      return variant;
    }
    if (strikePrice) {
      return 'strike';
    }
    return 'base';
  };

  return (
    <div className={twMerge('flex flex-col', className)}>
      {prefix && translations?.quantityPrefix && (
        <span className={twMerge(captionStyling, 'italic')}>{translations.quantityPrefix}</span>
      )}
      {quantity && translations?.quantityPrefix && (
        <span className={captionStyling}>{translations.quantityPrefix}</span>
      )}
      {!!strikePrice && (
        <span className="caption block">
          {translations?.strikePrice} <s>{formatPrice(strikePrice, locale)}</s>
        </span>
      )}
      {!!discountInfo && <span className="caption block py-2 font-bold">{discountInfo}</span>}
      {value !== undefined && (
        <span
          className={twMerge(PriceValueStyles({ priceVariant: getPriceStylingVariant(), size, font }), valueClassName)}
        >
          {formatPrice(value, locale)}
          {translations?.unitLabel ? <span className="text-sm">{`/${translations?.unitLabel}`}</span> : ''}
        </span>
      )}
      {quantity && (
        <span className={captionStyling}>
          {translations?.quantitySufix} {quantity}
        </span>
      )}

      {!!privatePrice && (
        <span className="caption text-price-private-price-text italic">{formatPrice(privatePrice, locale)}</span>
      )}
    </div>
  );
};

export default Price;
